/**
 * Minimap: canvas-based low-res overview of the whole map + player + guards
 * (marked or ghost). Only revealed tiles show; unrevealed are dark.
 */

import type { FovMemory } from '../sim/fovMemory';
import type { Guard } from '../sim/guard';
import { tileAt, type MapData } from '../sim/map';
import type { Player } from '../sim/player';
import { TileType } from '../sim/tiles';
import type { GameWindow } from './window';

/** Canvas pixels per tile. The canvas is sized to the FLOOR at this scale and
 *  then scaled to the panel by CSS (width:100%, height:auto), so the floor
 *  always fills the panel edge to edge with its aspect intact — no letterboxing
 *  and nothing to centre by hand. */
const CELL = 3;

export class Minimap {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly cell = CELL;
  /** Dimensions the canvas is currently sized for. */
  private fitFor = '';

  constructor(win: GameWindow, _map: MapData, _cellSize = 3) {
    this.canvas = document.createElement('canvas');
    // Sized per FLOOR in `fit()`. It used to be sized ONCE from whatever map
    // existed at construction — the tutorial floor — and never again, so every
    // generated floor was drawn into a stale canvas: clipped if bigger, adrift
    // in a corner if smaller, and never filling the panel.
    this.canvas.width = 1;
    this.canvas.height = 1;
    this.canvas.className = 'minimap-canvas';
    this.canvas.style.imageRendering = 'pixelated';
    win.body.appendChild(this.canvas);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('minimap: no 2d context');
    this.ctx = ctx;
  }

  /** Re-size the canvas to this floor. Cheap no-op unless the floor changed. */
  private fit(map: MapData): void {
    const key = `${map.width}x${map.height}`;
    if (key === this.fitFor) return;
    this.fitFor = key;
    this.canvas.width = map.width * this.cell;
    this.canvas.height = map.height * this.cell;
  }

  update(map: MapData, fov: FovMemory, player: Player, guards: readonly Guard[]): void {
    const c = this.ctx;
    this.fit(map);
    c.fillStyle = '#08080c';
    c.fillRect(0, 0, this.canvas.width, this.canvas.height);
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const idx = y * map.width + x;
        if (!fov.isSeen(idx)) continue;
        const t = tileAt(map, x, y);
        c.fillStyle = tileColor(t, fov.isCurrentlyVisible(idx));
        c.fillRect(x * this.cell, y * this.cell, this.cell, this.cell);
      }
    }
    for (const g of guards) {
      if (g.body.dead) continue;
      if (!g.everSeenByPlayer) continue;
      const idx = g.y * map.width + g.x;
      const inFov = fov.isCurrentlyVisible(idx);
      const gx = (inFov ? g.x : g.marked ? g.x : g.lastSeenX) * this.cell;
      const gy = (inFov ? g.y : g.marked ? g.y : g.lastSeenY) * this.cell;
      c.fillStyle = inFov ? '#d24040' : '#7a4040';
      c.fillRect(gx, gy, this.cell, this.cell);
    }
    // Player
    c.fillStyle = '#ffe060';
    c.fillRect(player.x * this.cell - 1, player.y * this.cell - 1, this.cell + 2, this.cell + 2);
  }
}

function tileColor(t: TileType, currentlyVisible: boolean): string {
  const bright = currentlyVisible;
  switch (t) {
    case TileType.Floor:
      return bright ? '#3a4048' : '#1c2028';
    case TileType.Wall:
      return bright ? '#7a8090' : '#3a3e46';
    case TileType.CubiclePartition:
      return bright ? '#a08c60' : '#4a4030';
    case TileType.Door:
    case TileType.DoorClosed:
      return bright ? '#c0a860' : '#5a5030';
    case TileType.AmmoPickup:
      return '#e0d060';
    case TileType.HealStation:
      return '#e07070';
    default:
      return '#08080c';
  }
}
